import { ILeaderboardStudent } from './lessonTasks/leaderboard';

export interface IBenchmarkedStat {
  value: number;
  median: number;
  /**
   * What a full bar means: the 90th percentile among students at the same
   * language and level over the same period. Deliberately not the cohort
   * maximum — one outlier would flatten every other bar — so a student in the
   * top tenth sits above their own scale and the bar is capped.
   */
  scale: number;
}

export interface IStreak {
  current: number;
  longest: number;
  activeToday: boolean;
}

/** What the period figures are counted over — the active language and level. */
export interface IProgressScope {
  /** Null while the student has no active target language. */
  languageName: string | null;
  /**
   * ISO code of that language. Carried alongside the name because several
   * languages share a display name in a given locale — "Norwegian" is both `no`
   * and `nb` in English — so a name alone cannot say which cohort is meant.
   * Absent from responses served before the code was added.
   */
  languageCode?: string | null;
  /** CEFR key from the backend, e.g. `a1`; `undefined` when none is set. */
  level: string;
}

export interface IProgressStats {
  xp: number;
  totalWordsLearned: number;
  totalRewardsEarned: number;
  streak: IStreak;
  timeSpent: IBenchmarkedStat;
  lessonsCompleted: IBenchmarkedStat;
  xpEarned: IBenchmarkedStat;
  weeklyActivity: Array<{
    day: string;
    hours: number;
  }>;
  /** Optional: absent from responses served before the scope was added. */
  scope?: IProgressScope;
}

export interface IReward {
  id: number;
  name: string;
  /** Backend-defined slug; the UI falls back for categories it has no art for. */
  category: string;
  imageURL: string | null;
  unlocked: boolean;
  unlockedAt: string | null;
  description?: string;
  /**
   * Milestone the badge is handed out for, counted in the unit its category
   * implies: words learned, XP earned, or seconds studied.
   */
  target: number;
  /**
   * Hex the badge is lit in, authored alongside its artwork. Absent on badges
   * minted before the field existed, which fall back to the category palette.
   */
  color?: string | null;
}

export interface IVocabularyProgress {
  level: string;
  levelLabel: string;
  wordsLearned: number;
  totalWords: number;
  percentage: number;
}

export interface IComparativeStat {
  /**
   * Null when the student has no completed lesson at their active level — a
   * different state from ranking last, which is a literal 0. Both need their
   * own sentence: formatting the ranked one with either value is what made the
   * banner print "invalid".
   */
  percentile: number | null;
}

/** One day of the current week, as the streak card draws it. */
export interface IStreakDay {
  /** ISO date, `YYYY-MM-DD`. Days after today are always inactive. */
  date: string;
  active: boolean;
}

export interface IGoalStreak {
  current: number;
  longest: number;
  activeToday: boolean;
  daysThisWeek: number;
  /** Always seven entries, Monday first. */
  week: IStreakDay[];
}

interface IGoalBase {
  id: string;
  /**
   * Where the bar starts. Reward goals begin at the previous badge on their
   * dimension, so `progress` is measured across that rung rather than from zero.
   */
  startValue: number;
  currentValue: number;
  target: number;
  progress: number;
  completed: boolean;
}

export interface IStreakGoal extends IGoalBase {
  kind: 'streak';
  title: string;
  description: string;
  streak: IGoalStreak;
}

/**
 * The badge being chased stays a surprise: the backend sends the dimension and
 * the colour it will be lit in, never its name, description or artwork. The
 * card labels itself from the category alone.
 */
export interface IRewardGoal extends IGoalBase {
  kind: 'reward';
  title: null;
  description: null;
  category: string;
  /** Hex authored with the badge; null falls back to the category palette. */
  color: string | null;
}

export type IGoal = IStreakGoal | IRewardGoal;

export type tStatsPeriod = 'week' | 'month' | 'year';

export type tMyProgressSection =
  | 'stats'
  | 'rewards'
  | 'vocabulary'
  | 'comparative'
  | 'goals';

export type tMyProgressFlags = Record<tMyProgressSection, boolean>;

export interface IMyProgressState {
  stats: IProgressStats | null;
  /** Period the stored stats belong to, so the picker label survives a remount
      and can never describe data from another period. */
  statsPeriod: tStatsPeriod;
  /** Newest in-flight stats request — older responses are ignored. */
  statsRequestId: string | null;
  rewards: IReward[];
  vocabulary: IVocabularyProgress | null;
  comparative: IComparativeStat | null;
  goals: IGoal[];
  leaderboard: ILeaderboardState;
  loading: tMyProgressFlags;
  /** Set once a request settles (fulfilled or rejected) so mounts don't refetch. */
  fetched: tMyProgressFlags;
  /** Section failed to load — the UI must say so instead of rendering zeroes. */
  failed: tMyProgressFlags;
}

export type tLeaderboardDimension = 'time_spent' | 'lessons_completed' | 'xp_earned';

export interface ILeaderboardEntry {
  rank: number;
  value: number;
  student: ILeaderboardStudent;
}

export interface ILeaderboardStanding {
  /** Null when the student has no activity in the period. */
  rank: number | null;
  total: number;
  value: number;
  /** True when the student hid their own profile from other people's boards. */
  hidden: boolean;
}

export interface ILeaderboardBoard {
  dimension: tLeaderboardDimension;
  period: tStatsPeriod;
  scope: IProgressScope;
  me: ILeaderboardStanding;
  rows: ILeaderboardEntry[];
}

/** One board per dimension-and-period pair, so flipping a toggle back is free. */
export function leaderboardKey(
  dimension: tLeaderboardDimension,
  period: tStatsPeriod
): string {
  return `${dimension}:${period}`;
}

export interface ILeaderboardState {
  boards: Record<string, ILeaderboardBoard>;
  /** Key of the pair in flight, or null. */
  loading: string | null;
  /** Key of the pair whose request failed, or null. */
  failed: string | null;
}
